/**
 * Feed — org-wide social feed
 *
 * Three panels via top chips:
 *   "Feed"          — posts (announcements + posts + appreciation reposts)
 *   "Appreciations" — my received + given appreciations
 *   "Feedback"      — my received + given feedback
 *
 * Audience targeting for posts: All | Specific Users | Department | Designation
 * Auto-cycle selection: active review cycle auto-selected based on today's date
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useApi }       from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { useSpacesUpload, validateFiles, ACCEPT_STRING } from '../../lib/useSpacesUpload';
import { AttachmentDisplay, PendingFilePreview, AttachmentModal } from '../../components/shared/AttachmentComponents';
import Modal from '../../components/Modal';
import RichEditor, { EmojiPicker, FRAME_IMAGES } from '../../components/workspace/RichEditor';
import MentionTextarea from '../../components/workspace/MentionTextarea';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';
import { X } from 'lucide-react';
import '../../styles/Feed.css';

// ─── helpers ──────────────────────────────────────────────────────────────────

function uname(u) {
  if (!u) return 'Unknown';
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
}

function joinNames(names) {
  if (!names || names.length === 0) return '';
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
}

// Shows up to 2 names inline; clicking +N expands to show all names with & before last.
function NamesWithMore({ names }) {
  const [expanded, setExpanded] = useState(false);
  if (!names || names.length === 0) return null;
  if (expanded || names.length <= 2) return <span>{joinNames(names)}</span>;
  const rest = names.length - 2;
  return (
    <span>
      {joinNames(names.slice(0, 2))}
      <button
        className="feed-names-more-btn"
        onClick={e => { e.stopPropagation(); setExpanded(true); }}
      >+{rest}</button>
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '';
  const ts = /[zZ]|[+-]\d\d:?\d\d$/.test(d) ? d : d + 'Z';
  const dt = new Date(ts);
  if (isNaN(dt.getTime())) return '';
  const date = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} at ${time}`;
}

/** Find the review cycle that is active today; fall back to most recent. */
function findActiveCycle(cycles) {
  if (!cycles.length) return null;
  const today = new Date().toISOString().split('T')[0];
  const active = cycles.find(c => c.start_date <= today && c.end_date >= today);
  return active || cycles[cycles.length - 1];
}

const BADGE_META = {
  teamwork:        { emoji: '🤝', label: 'Teamwork' },
  innovation:      { emoji: '💡', label: 'Innovation' },
  leadership:      { emoji: '🌟', label: 'Leadership' },
  excellence:      { emoji: '🏆', label: 'Excellence' },
  mentorship:      { emoji: '🎓', label: 'Mentorship' },
  customer_focus:  { emoji: '💛', label: 'Customer Focus' },
  problem_solving: { emoji: '🔧', label: 'Problem Solving' },
};

const EMOJIS = ['👍', '❤️', '🎉', '👏', '🔥'];

function FeedReactionPill({ r, names, iReacted, onReact }) {
  const [visible, setVisible] = useState(false);
  const [above, setAbove] = useState(false);
  const wrapRef = useRef(null);
  const hideTimer = useRef(null);

  const show = () => {
    clearTimeout(hideTimer.current);
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      setAbove(spaceAbove >= 20 && spaceAbove >= spaceBelow);
    }
    setVisible(true);
  };
  const hide = () => { hideTimer.current = setTimeout(() => setVisible(false), 150); };

  return (
    <div ref={wrapRef} className="feed-rxn-wrap" onMouseEnter={show} onMouseLeave={hide}>
      <button
        onClick={onReact}
        className={`feed-rxn-btn${iReacted ? ' feed-rxn-btn--active' : ''}`}
      >{r.emoji} {r.count}</button>
      {names.length > 0 && visible && (
        <div
          className={`feed-rxn-tooltip${above ? ' above' : ' below'}`}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div className="feed-rxn-tooltip-header">
            <span>{r.emoji}</span>
            <span>{r.count} {r.count === 1 ? 'person' : 'people'}</span>
          </div>
          <div className="feed-rxn-tooltip-list">
            {names.map((n, i) => (
              <div key={i} className="feed-rxn-tooltip-item">{n}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Multi-select checkbox list ───────────────────────────────────────────────

function MultiSelectList({ items, selected, onChange, labelKey = 'name', valueKey = 'id', placeholder = 'Search…' }) {
  const [search, setSearch] = useState('');

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
  };

  const filtered = search.trim()
    ? items.filter(i => (i[labelKey] || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  const allFilteredIds = filtered.map(i => i[valueKey]);
  const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selected.includes(id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      onChange(selected.filter(id => !allFilteredIds.includes(id)));
    } else {
      onChange([...new Set([...selected, ...allFilteredIds])]);
    }
  };

  return (
    <div className="feed-multiselect">
      <div className="feed-multiselect-search">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={placeholder}
        />
      </div>
      {filtered.length > 0 && (
        <div className="feed-multiselect-all" onClick={toggleAll} style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={allFilteredSelected}
            onChange={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); toggleAll(); }}
            style={{ accentColor: 'var(--brand)', cursor: 'pointer' }}
          />
          <span>{allFilteredSelected ? 'Deselect All' : 'Select All'}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontWeight: 400 }}>{selected.length}/{items.length}</span>
        </div>
      )}
      <div className="feed-multiselect-list" style={{ maxHeight: 108, overflowY: 'auto' }}>
        {filtered.length === 0 && <div className="feed-multiselect-no-results">No results</div>}
        {filtered.map(item => (
          <label key={item[valueKey]} className={`feed-multiselect-item${selected.includes(item[valueKey]) ? ' feed-multiselect-item--checked' : ''}`}>
            <input type="checkbox" checked={selected.includes(item[valueKey])} onChange={() => toggle(item[valueKey])} style={{ accentColor: 'var(--brand)', cursor: 'pointer' }} />
            <span>{item[labelKey]}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Audience badge (shown on feed cards) ─────────────────────────────────────

function AudienceBadge({ post, members, departments, roles }) {
  const [expanded, setExpanded] = useState(false);

  if (!post.audience_type || post.audience_type === 'all') return null;
  const ids = JSON.parse(post.audience_ids || '[]');

  let allNames = [];
  if (post.audience_type === 'users') {
    allNames = ids.map(id => {
      const m = members.find(m => m.id === id);
      return m ? uname(m) : `#${id}`;
    });
  } else if (post.audience_type === 'departments') {
    allNames = ids.map(id => {
      const d = departments.find(d => d.id === id);
      return d ? d.name : `dept#${id}`;
    });
  } else if (post.audience_type === 'roles') {
    allNames = ids.map(id => {
      const r = roles.find(r => r.id === id);
      return r ? r.title : `role#${id}`;
    });
  }

  const icons = { users: '👤', departments: '🏢', roles: '💼' };
  const showAll = expanded || allNames.length <= 2;
  const restCount = allNames.length - 2;

  return (
    <span className="feed-audience-badge">
      {icons[post.audience_type]}{' '}
      {showAll
        ? joinNames(allNames)
        : <>
            {joinNames(allNames.slice(0, 2))}
            <button
              className="feed-audience-more-btn"
              onClick={e => { e.stopPropagation(); setExpanded(true); }}
            >+{restCount}</button>
          </>
      }
    </span>
  );
}

// ─── Rich content renderer (HTML from RichEditor) ────────────────────────────

const PURIFY_CONFIG = {
  ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'color', 'size',
                 'data-user-id', 'data-task-id', 'contenteditable', 'target', 'rel',
                 'colspan', 'rowspan', 'align',
                 // Inline <video> attrs — without these, DOMPurify strips them and the
                 // saved video renders with no play controls (image-first-frame only).
                 'controls', 'muted', 'playsinline', 'preload', 'poster',
                 // Inline non-image/video file chips (RichEditor's
                 // buildRemovableFileChip) — need the file's URL/name/type to
                 // survive sanitization so the chip stays clickable/labeled
                 // (and its preview modal picks the right viewer) in the
                 // read-only feed view, not just while editing.
                 'data-file-url', 'data-file-name', 'data-content-type'],
  ADD_TAGS: ['span', 'font', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
};

marked.use({ gfm: true, breaks: true });

// Convert @[id:name] tokens → mention spans, then run through marked for markdown rendering
function renderMarkdownContent(text) {
  if (!text) return { __html: '' };
  const withMentions = text.replace(
    /@\[(\d+):([^\]]*)\]/g,
    (_, id, name) => `<span class="mention-chip" data-user-id="${id}">@${name}</span>`
  );
  const html = marked.parse(withMentions);
  return { __html: DOMPurify.sanitize(html, PURIFY_CONFIG) };
}

// Detect if HTML content (from RichEditor) wraps plain markdown text
function extractMarkdownFromHtml(html) {
  const text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return text;
}

function hasMarkdownSyntax(html) {
  const text = extractMarkdownFromHtml(html);
  return /^\|.+\|/m.test(text) || /^#{1,6}\s/m.test(text);
}

function linkifyHtml(html) {
  // Only linkify URLs that are in text content — split on tags so we don't touch href= values
  return html.replace(/(>|^)([^<]*)(<|$)/g, (_, pre, text, post) => {
    const linked = text.replace(/(\bhttps?:\/\/[^\s<>"]+)/g,
      url => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    return pre + linked + post;
  });
}

function mentionifyHtml(html) {
  // Highlight bare @Word mentions in text nodes that weren't selected from the dropdown
  return html.replace(/(>|^)([^<]*)(<|$)/g, (_, pre, text, post) => {
    const highlighted = text.replace(/(?<![="])@([\w.]+)/g,
      (_, name) => `<span class="mention-user">@${name}</span>`);
    return pre + highlighted + post;
  });
}

function RichContent({ html }) {
  const navigate = useNavigate();
  const [previewAtt, setPreviewAtt] = useState(null);
  const handleClick = useCallback((e) => {
    // Inline non-image/video file chips (RichEditor's buildRemovableFileChip)
    // aren't links — open an in-app preview (same modal used everywhere else
    // attachments are shown, with its own Download button) instead of
    // forcing a browser download/new-tab.
    const chipWrapper = e.target.closest('.cmt-inline-img-wrap');
    if (chipWrapper?.dataset.fileUrl) {
      setPreviewAtt({
        file_url:     chipWrapper.dataset.fileUrl,
        file_name:    chipWrapper.dataset.fileName,
        content_type: chipWrapper.dataset.contentType,
      });
      return;
    }
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (href && href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  }, [navigate]);

  const raw = html || '';
  let safeHtml;
  if (hasMarkdownSyntax(raw)) {
    const text = extractMarkdownFromHtml(raw);
    const mdHtml = marked.parse(text);
    safeHtml = DOMPurify.sanitize(mdHtml, PURIFY_CONFIG);
  } else {
    safeHtml = DOMPurify.sanitize(mentionifyHtml(linkifyHtml(raw)), PURIFY_CONFIG);
  }

  return (
    <>
      {previewAtt && <AttachmentModal attachment={previewAtt} onClose={() => setPreviewAtt(null)} />}
      <div
        className="rich-post-content"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
        onClick={handleClick}
      />
    </>
  );
}

// ─── Comments Section ──────────────────────────────────────────────────────────

function CommentsSection({ appId, postId, me, members, initialCount }) {
  const api = useApi();
  const [open,         setOpen]         = useState(false);
  const [comments,     setComments]     = useState([]);
  const [count,        setCount]        = useState(initialCount || 0);
  const [text,         setText]         = useState('');
  const [error,        setError]        = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [posting,      setPosting]      = useState(false);
  const [visibleCount, setVisibleCount] = useState(15);
  const [showEmoji,    setShowEmoji]    = useState(false);
  const emojiButtonRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const d = await api.getFeedComments(appId, postId);
      const items = d.items || [];
      setComments(items);
      setCount(items.length);
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [api, appId, postId]);

  const toggle = () => {
    if (!open) load();
    setOpen(v => !v);
  };

  const submit = async () => {
    if (!text.trim()) return;
    const savedText = text.trim();
    const optimisticComment = {
      id: `temp-${Date.now()}`,
      content: savedText,
      user_id: me,
      user: members.find(m => m.id === me) || { first_name: 'You', last_name: '' },
      created_at: new Date().toISOString(),
    };

    setComments(prev => [...prev, optimisticComment]);
    setCount(prev => prev + 1);
    setText('');
    setPosting(true);
    setError(null);

    try {
      await api.addFeedComment(appId, postId, savedText);
      await load(true);
    } catch (err) {
      setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
      setCount(prev => prev - 1);
      setError(err.response?.data?.error || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    const original = comments;
    setComments(prev => prev.filter(c => c.id !== commentId));
    setCount(prev => Math.max(0, prev - 1));
    try {
      await api.deleteFeedComment(appId, postId, commentId);
      await load(true);
    } catch (err) {
      setComments(original);
      setCount(original.length);
      setError(err.response?.data?.error || 'Failed to delete comment');
    }
  };

  return (
    <div className="feed-comments">
      <button className="feed-comments-toggle" onClick={toggle}>
        <span>💬</span>
        <span>{count > 0 ? `${count} comment${count !== 1 ? 's' : ''}` : 'Comment'}</span>
        <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="feed-comments-area">
          {error && <div className="feed-comment-error">{error}</div>}
          {loading && <div className="feed-comment-loading">Loading…</div>}

          {!loading && comments.length === 0 && !error && (
            <div className="feed-comment-empty">No comments yet — be the first!</div>
          )}

          {!loading && comments.slice(0, visibleCount).map(c => (
            <div key={c.id} className="feed-comment-item">
              <div className="feed-avatar feed-avatar--sm">
                {c.user?.photo_url
                  ? <img src={c.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : uname(c.user).charAt(0).toUpperCase()}
              </div>
              <div className="feed-comment-body">
                <div className="feed-comment-name-row">
                  <span className="feed-comment-author">{uname(c.user)}</span>
                  <span className="feed-comment-date">{fmtDate(c.created_at)}</span>
                  {c.user_id === me && (
                    <button className="feed-comment-del" onClick={() => handleDelete(c.id)} title="Delete">✕</button>
                  )}
                </div>
                <div className="feed-comment-text feed-comment-text--md" dangerouslySetInnerHTML={renderMarkdownContent(c.content)} />
              </div>
            </div>
          ))}
          {!loading && comments.length > visibleCount && (
            <div style={{ marginBottom: 6, marginLeft: 40 }}>
              <button className="feed-panel-viewall" onClick={() => setVisibleCount(comments.length)}>
                View More
              </button>
            </div>
          )}
          {!loading && visibleCount > 15 && (
            <div style={{ marginBottom: 6, marginLeft: 40 }}>
              <button className="feed-panel-viewall" onClick={() => setVisibleCount(15)}>
                View Less
              </button>
            </div>
          )}

          <div style={{ marginTop: 4, position: 'relative' }}>
            <MentionTextarea
              value={text}
              onChange={setText}
              members={members || []}
              placeholder="Write a comment… (@ to mention, Enter to post)"
              rows={1}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              style={{
                width: '100%', border: '1px solid var(--border)', borderRadius: 8,
                padding: '8px 32px 8px 12px', fontSize: 13, resize: 'none',
                fontFamily: 'inherit', outline: 'none', lineHeight: 1.5,
                boxSizing: 'border-box', background: 'var(--bg-card)', color: 'var(--text-primary)',
              }}
            />
            <div ref={emojiButtonRef} style={{ position: 'absolute', top: 6, right: 6 }}>
              <button
                type="button"
                onClick={() => setShowEmoji(v => !v)}
                title="Insert emoji"
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 16, lineHeight: 1, padding: '2px 4px', borderRadius: 4,
                }}
              >
                😊
              </button>
              {showEmoji && (
                <EmojiPicker
                  anchorRef={emojiButtonRef}
                  onSelect={emoji => { setText(prev => prev + emoji); setShowEmoji(false); }}
                  onClose={() => setShowEmoji(false)}
                />
              )}
            </div>
            <div className="feed-comment-input-row">
              <span className="feed-comment-hint">@ mention · Enter to post</span>
              <button
                onClick={submit}
                disabled={posting || !text.trim()}
                className={`feed-comment-submit${text.trim() ? ' feed-comment-submit--active' : ''}`}
              >
                {posting ? '…' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AppreciationLetterCard ────────────────────────────────────────────────────

const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '');
function fullLogoUrl(path) { return path ? `${API_HOST}${path}` : null; }

function AppreciationLetterCard({ appr, organisation }) {
  const { from_user, to_user, to_users, badge, message } = appr;
  const badgeMeta = badge ? BADGE_META[badge] : null;
  const logoSrc = organisation?.logo_data || fullLogoUrl(organisation?.logo_url);
  const fromName = uname(from_user);
  const allToUsers = (to_users && to_users.length) ? to_users : (to_user ? [to_user] : []);
  const allToNames = allToUsers.length ? allToUsers.map(u => uname(u)) : (to_user ? [uname(to_user)] : []);
  const toFirst  = allToUsers[0]?.first_name || allToNames[0]?.split(' ')[0] || '';
  const fromInitial = fromName.charAt(0).toUpperCase();

  return (
    <div className="appr-letter-card">
      {/* Accent stripe */}
      <div className="appr-letter-accent" />

      <div className="appr-letter-inner">
        {/* Header: sender (left) + date + org logo (right) */}
        <div className="appr-letter-header">
          <div className="appr-letter-sender">
            {from_user?.photo_url ? (
              <img src={from_user.photo_url} alt={fromName} className="appr-letter-avatar" />
            ) : (
              <div className="appr-letter-avatar appr-letter-avatar--initials">{fromInitial}</div>
            )}
            <div className="appr-letter-sender-info">
              <span className="appr-letter-sender-name">{fromName}</span>
              <span className="appr-letter-sender-role">
                {from_user?.role_title || 'Team Member'}
              </span>
            </div>
          </div>
          <div className="appr-letter-org">
            <span className="appr-letter-org-date">{fmtDate(appr.created_at)}</span>
            {logoSrc ? (
              <img src={logoSrc} alt={organisation?.name || 'Logo'} className="appr-letter-org-logo" />
            ) : (
              <span className="appr-letter-org-name">{organisation?.name || ''}</span>
            )}
          </div>
        </div>

        <div className="appr-letter-divider" />

        {/* Letter body */}
        <div className="appr-letter-body">
          <p className="appr-letter-salutation">Dear <NamesWithMore names={allToNames} />,</p>
          <div className="appr-letter-message"><RichContent html={message} /></div>
          {appr.attachments?.length > 0 && <AttachmentDisplay attachments={appr.attachments} />}
        </div>

        {/* Footer: badge only */}
        <div className="appr-letter-footer">
          <div className="appr-letter-badge">
            {badgeMeta ? (
              <>
                <span className="appr-letter-badge-icon">{badgeMeta.emoji}</span>
                <span className="appr-letter-badge-label">{badgeMeta.label}</span>
              </>
            ) : (
              <span className="appr-letter-badge-icon">🏅</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FeedbackLetterCard ─────────────────────────────────────────────────────────

function FeedbackLetterCard({ fb }) {
  const allToUsers = (fb.to_users && fb.to_users.length) ? fb.to_users : (fb.to_user ? [fb.to_user] : []);
  const allToNames = allToUsers.map(u => uname(u));

  return (
    <div className="feed-feedback-card">
      <div className="feed-feedback-from">
        {fb.is_anonymous
          ? <em style={{ color: 'var(--text-muted)' }}>Anonymous feedback</em>
          : <strong>{uname(fb.from_user)}</strong>}
        {' '}gave feedback to <strong><NamesWithMore names={allToNames} inline /></strong>
        <span className="feed-feedback-date" style={{ marginLeft: 8 }}>{fmtDate(fb.created_at)}</span>
      </div>
      <div className="feed-feedback-body">
        <RichContent html={fb.feedback_text} />
        {fb.attachments?.length > 0 && <AttachmentDisplay attachments={fb.attachments} />}
      </div>
    </div>
  );
}

// ─── FeedCard ─────────────────────────────────────────────────────────────────

const typeLabel = { announcement: '📢 Announcement', appreciation: '🙌 Appreciation', post: '💬 Post', poll: '📊 Poll', feedback: '💬 Feedback' };

// Optional decorative border around a post — .feed-card already paints its
// own opaque background, so the pattern only needs to live in the padding
// ring around it; no separate "inner" wrapper is needed. Undecorated posts
// (the default) render exactly as before — no extra DOM, no visual change.
// The image itself is set inline (imported, not a public/ url()) because a
// plain CSS `url('/assets/...')` gets resolved by css-loader as a module
// request relative to the CSS file and fails the production build.
function FrameWrap({ frameStyle, children }) {
  if (!frameStyle) return <>{children}</>;
  return (
    <div className={`feed-frame feed-frame--${frameStyle}`} style={{ borderImageSource: `url(${FRAME_IMAGES[frameStyle]})` }}>
      {children}
    </div>
  );
}

function FeedCard({ post, me, isAdmin, members, departments, roles, onDelete, onPin, onReact, onEdit, onVote, appId }) {
  const [showEmojis, setShowEmojis] = useState(false);
  const emojiRef = useRef(null);
  const { organisation } = useWorkspace();

  const ageMs = Date.now() - new Date(post.created_at).getTime();
  const canEdit = post.author_user_id === me && ageMs < 15 * 60 * 1000;
  const isLetter = post.post_type === 'appreciation' || post.post_type === 'feedback';

  useEffect(() => {
    function handleClick(e) {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmojis(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <FrameWrap frameStyle={post.frame_style}>
    <div className={`feed-card${post.pinned ? ' feed-card--pinned' : ''}`}>
      {/* Header — appreciation/feedback posts show only type badge + actions */}
      <div className="feed-card-header">
        {!isLetter && (
          <div className="feed-avatar">
            {post.author?.photo_url
              ? <img src={post.author.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : uname(post.author).charAt(0).toUpperCase()}
          </div>
        )}
        <div className="feed-card-meta">
          <div className="feed-card-name-row">
            {!isLetter && (
              <span className="feed-author-name">{uname(post.author)}</span>
            )}
            <span className={`feed-type-badge feed-type-badge--${post.post_type}`}>{typeLabel[post.post_type]}</span>
            {!!post.pinned && <span className="feed-pinned-badge">📌 Pinned</span>}
            <AudienceBadge post={post} members={members} departments={departments} roles={roles} />
          </div>
          {!isLetter && (
            <div className="feed-card-date">{fmtDate(post.created_at)}</div>
          )}
        </div>
        <div className="feed-card-actions">
          {isAdmin && post.post_type !== 'feedback' && (
            <button onClick={() => onPin(post.id)} className="feed-ghost-btn" title={post.pinned ? 'Unpin' : 'Pin'}>
              {post.pinned ? '📌' : '📍'}
            </button>
          )}
          {canEdit && (post.post_type !== 'appreciation' || post.appreciation) && (post.post_type !== 'feedback' || post.feedback) && (
            <button onClick={() => onEdit(post)} className="feed-ghost-btn" title="Edit (within 15 min)">✏️</button>
          )}
          {(post.author_user_id === me || isAdmin) && (
            <button onClick={() => onDelete(post.id)} className="feed-ghost-btn feed-ghost-btn--danger" title="Delete">🗑</button>
          )}
        </div>
      </div>

      {/* Appreciation letter card */}
      {post.post_type === 'appreciation' && post.appreciation && (
        <AppreciationLetterCard appr={post.appreciation} organisation={organisation} />
      )}

      {/* Feedback letter card */}
      {post.post_type === 'feedback' && post.feedback && (
        <FeedbackLetterCard fb={post.feedback} />
      )}

      {/* Rich HTML content */}
      {!isLetter && post.content && (
        <RichContent html={post.content} />
      )}

      {/* Poll */}
      {post.post_type === 'poll' && post.poll && (
        <PollCard poll={post.poll} onVote={optionIds => onVote(post.id, optionIds)} />
      )}

      {/* Attachments */}
      <AttachmentDisplay attachments={post.attachments} />

      {/* Reactions + Comment toggle — not applicable to feedback (private, no engagement) */}
      {post.post_type !== 'feedback' && (
        <div className="feed-reactions">
          {(post.reactions || []).filter(r => r.emoji && r.count > 0).map(r => {
            const names = r.user_names?.length ? r.user_names : [];
            return (
              <FeedReactionPill
                key={r.emoji}
                r={r}
                names={names}
                iReacted={post.my_reactions?.includes(r.emoji)}
                onReact={() => onReact(post.id, r.emoji)}
              />
            );
          })}
          <div ref={emojiRef} className="feed-emoji-picker-wrap">
            <button onClick={() => setShowEmojis(v => !v)} className="feed-ghost-btn" style={{ fontSize: 16 }} title="React">😊</button>
            {showEmojis && (
              <div className="feed-emoji-picker">
                {EMOJIS.map(e => (
                  <button key={e}
                    onClick={() => { onReact(post.id, e); setShowEmojis(false); }}
                    className="feed-emoji-btn"
                  >{e}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comments — not applicable to feedback (private, no discussion thread) */}
      {post.post_type !== 'feedback' && (
        <CommentsSection appId={appId} postId={post.id} me={me} members={members} initialCount={post.comment_count || 0} />
      )}
    </div>
    </FrameWrap>
  );
}

// ─── PollCard ─────────────────────────────────────────────────────────────────

function PollCard({ poll, onVote }) {
  const [voting,       setVoting]       = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  const myVotes = poll.my_votes || [];
  const total   = poll.total_votes || 0;

  const handleClick = async (optionId) => {
    if (voting) return;
    const next = poll.allow_multiple
      ? (myVotes.includes(optionId) ? myVotes.filter(id => id !== optionId) : [...myVotes, optionId])
      : (myVotes.includes(optionId) ? [] : [optionId]);
    setVoting(true);
    try { await onVote(next); } finally { setVoting(false); }
  };

  return (
    <div className="feed-poll-card">
      <div className="feed-poll-question">{poll.question}</div>
      <div className="feed-poll-options">
        {poll.options.map(opt => {
          const pct    = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
          const mine   = myVotes.includes(opt.id);
          const voters = opt.voters || [];
          const shown  = voters.slice(0, 3);
          const overflow = voters.length - shown.length;
          return (
            <div
              key={opt.id}
              role="button"
              tabIndex={0}
              className={`feed-poll-option${mine ? ' feed-poll-option--mine' : ''}${voting ? ' feed-poll-option--voting' : ''}`}
              onClick={() => handleClick(opt.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(opt.id); } }}
            >
              <div className="feed-poll-option-fill" style={{ width: `${pct}%` }} />
              <span className="feed-poll-option-text">{mine && '✓ '}{opt.option_text}</span>
              <div className="feed-poll-option-right">
                {voters.length > 0 && (
                  <button
                    type="button"
                    className="feed-poll-option-voters"
                    onClick={e => { e.stopPropagation(); setActiveOption(opt); }}
                    title={`${voters.length} voted for this option`}
                  >
                    {shown.map(v => <PollAvatar key={v.id} user={v} size={18} />)}
                    {overflow > 0 && <div className="feed-poll-voter-avatar feed-poll-voter-more">+{overflow}</div>}
                  </button>
                )}
                <span className="feed-poll-option-pct">{opt.votes} · {pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="feed-poll-meta">
        {total} {total === 1 ? 'vote' : 'votes'}{poll.allow_multiple ? ' · Multiple choice' : ''}
      </div>
      {activeOption && (
        <PollVotersModal
          title={`${activeOption.option_text} · ${(activeOption.voters || []).length} ${(activeOption.voters || []).length === 1 ? 'vote' : 'votes'}`}
          voters={activeOption.voters || []}
          onClose={() => setActiveOption(null)}
        />
      )}
    </div>
  );
}

function PollAvatar({ user, size = 28 }) {
  const color = AVATAR_COLORS[(user.id || 0) % AVATAR_COLORS.length];
  return (
    <div
      className="feed-poll-voter-avatar"
      style={{ width: size, height: size, background: user.photo_url ? 'transparent' : color }}
      title={user.name}
    >
      {user.photo_url
        ? <img src={user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        : (user.name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function PollVotersModal({ title, voters, onClose }) {
  return (
    <Modal open title={title || `${voters.length} ${voters.length === 1 ? 'Vote' : 'Votes'}`} onClose={onClose} size="sm" className="feed-poll-voters-modal">
      <div className="feed-poll-voters-list">
        {voters.map(v => (
          <div key={v.id} className="feed-poll-voters-list-row">
            <PollAvatar user={v} size={32} />
            <span>{v.name}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ─── Write Post Modal ─────────────────────────────────────────────────────────

function PostModal({ appId, isAdmin, me, members, departments, roles, tasks, initialType, onDone, onClose }) {
  const api = useApi();
  const postType = initialType === 'poll' ? 'poll' : 'post';
  const [content,       setContent]       = useState('');
  const [pollQuestion,  setPollQuestion]  = useState('');
  const [pollOptions,   setPollOptions]   = useState(['', '']);
  const [pollMulti,     setPollMulti]     = useState(false);
  const [audienceType,  setAudienceType]  = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [fileErrors,    setFileErrors]    = useState([]);
  const [frameStyle,    setFrameStyle]    = useState(null);
  const feedFileRef    = useRef(null);
  const richEditorRef  = useRef(null);
  const { uploadFiles, uploading } = useSpacesUpload(appId, 'feed');
  const [saving,        setSaving]        = useState(false);
  const [err,           setErr]           = useState('');

  const addFiles = (incoming) => {
    const { valid, errors } = validateFiles(incoming);
    setFileErrors(errors);
    // Images embed inline at the cursor (default insertFiles behavior) so
    // the compose view stays truthful — whatever position you see while
    // typing is where it actually ends up after posting. Non-image files
    // still can't be inlined, so they stay as regular attachments below.
    if (valid.length) richEditorRef.current?.insertFiles(valid);
  };

  useEffect(() => {
    const input = feedFileRef.current;
    if (!input) return;
    const handler = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      e.target.value = '';
    };
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, []);

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  };

  const audienceIds = () => {
    if (audienceType === 'users')       return selectedUsers;
    if (audienceType === 'departments') return selectedDepts;
    if (audienceType === 'roles')       return selectedRoles;
    return [];
  };

  const audienceValid = () => {
    if (audienceType === 'all') return true;
    return audienceIds().length > 0;
  };

  const updatePollOption = (i, val) => setPollOptions(prev => prev.map((o, idx) => idx === i ? val : o));
  const addPollOption    = () => setPollOptions(prev => prev.length < 10 ? [...prev, ''] : prev);
  const removePollOption = (i) => setPollOptions(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    // Pull pending files out first — this also strips their inline preview
    // wrappers (blob: URLs + remove button) from the editor DOM so they
    // never end up baked into saved content; getValue() below reads the
    // now-cleaned HTML.
    const inlineFiles = richEditorRef.current?.extractFiles() || [];
    const cleanedContent = (richEditorRef.current?.getValue() ?? content)
      .trim().replace(/(<div>\s*<br\s*\/?>\s*<\/div>|<br\s*\/?>)+$/gi, '');
    let pollPayload = {};

    if (postType === 'poll') {
      const question = pollQuestion.trim();
      const opts     = pollOptions.map(o => o.trim()).filter(Boolean);
      if (!question) return setErr('Add a poll question');
      if (opts.length < 2) return setErr('Add at least 2 options');
      const noteLen = cleanedContent.replace(/<[^>]+>/g, '').length;
      if (noteLen > 1000) return setErr('Note must be under 1000 characters');
      pollPayload = { poll_question: question, poll_options: opts, poll_multi: pollMulti };
    } else if (!cleanedContent.replace(/<[^>]+>/g, '').trim() && !/<img\b|<video\b/i.test(cleanedContent) && inlineFiles.length === 0) {
      // Checking the raw HTML string (tags included) rather than its
      // stripped text meant a post left visually empty by select-all-delete
      // — which browsers usually leave as an empty <p></p>/<div><br></div>,
      // not a true '' string — read as "has content" and saved anyway.
      return setErr('Write something or attach a file');
    }
    if (!audienceValid()) return setErr('Please select at least one recipient');

    setSaving(true); setErr('');
    try {
      let attachments = [];
      if (inlineFiles.length > 0) {
        attachments = await uploadFiles(inlineFiles);
      }
      await api.createPost(appId, {
        content: cleanedContent,
        post_type: postType,
        audience_type: audienceType,
        audience_ids: audienceIds(),
        attachments,
        frame_style: frameStyle,
        ...pollPayload,
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to post');
    } finally { setSaving(false); }
  };

  const audienceOptions = [
    { value: 'all',         label: '🌐 Everyone',         hint: 'Visible to all workspace members' },
    { value: 'users',       label: '👤 Specific People',  hint: 'Choose one or more members' },
    // { value: 'departments', label: '🏢 Department(s)',    hint: 'Everyone in selected departments' },
    // { value: 'roles',       label: '💼 Designation(s)',   hint: 'Everyone with selected designations' },
  ];

  const memberItems = members.filter(m => m.id !== me).map(m => ({ ...m, name: uname(m) }));
  const deptItems   = departments.map(d => ({ ...d }));
  const roleItems   = roles.map(r => ({ ...r, name: r.title }));
  const noteLen     = content.replace(/<[^>]+>/g, '').length;

  return (
    <Modal open title={postType === 'poll' ? 'Create a Poll' : 'Write a Post'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Poll composer */}
        {postType === 'poll' && (
          <>
            <div>
              <div className="feed-modal-msg-label">Question <span className="feed-modal-required">*</span></div>
              <input
                type="text"
                className="feed-poll-question-input"
                value={pollQuestion}
                onChange={e => setPollQuestion(e.target.value)}
                placeholder="Ask your team something…"
                maxLength={300}
              />
            </div>
            <div>
              <div className="feed-modal-msg-label">Options</div>
              <div className="feed-poll-options-edit">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="feed-poll-option-edit-row">
                    <input
                      type="text"
                      value={opt}
                      onChange={e => updatePollOption(i, e.target.value)}
                      placeholder={`Option ${i + 1}`}
                      maxLength={200}
                    />
                    {i >= 2 && (
                      <button type="button" className="feed-ghost-btn feed-ghost-btn--danger feed-poll-option-remove" onClick={() => removePollOption(i)} title="Remove option">✕</button>
                    )}
                  </div>
                ))}
              </div>
              {pollOptions.length < 10 && (
                <button type="button" className="feed-poll-add-option-btn" onClick={addPollOption}>+ Add option</button>
              )}
            </div>
            <label className="feed-poll-multi-toggle">
              <input type="checkbox" checked={pollMulti} onChange={e => setPollMulti(e.target.checked)} />
              Allow selecting multiple options
            </label>
          </>
        )}

        {/* Audience selector */}
        <div>
          <div className="feed-modal-section-label">Who can see this?</div>
          <div className="feed-modal-audience-opts">
            {audienceOptions.map(opt => (
              <label key={opt.value} className={`feed-modal-audience-opt${audienceType === opt.value ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="audienceType"
                  value={opt.value}
                  checked={audienceType === opt.value}
                  onChange={() => setAudienceType(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {audienceType !== 'all' && (
            <div className="feed-modal-audience-hint">
              {audienceOptions.find(o => o.value === audienceType)?.hint}
            </div>
          )}
        </div>

        {/* Audience picker */}
        {audienceType === 'users' && (
          <MultiSelectList items={memberItems} selected={selectedUsers} onChange={setSelectedUsers} labelKey="name" valueKey="id" placeholder="Search Members…" />
        )}
        {audienceType === 'departments' && (
          <MultiSelectList items={deptItems} selected={selectedDepts} onChange={setSelectedDepts} labelKey="name" valueKey="id" placeholder="Search Departments…" />
        )}
        {audienceType === 'roles' && (
          <MultiSelectList items={roleItems} selected={selectedRoles} onChange={setSelectedRoles} labelKey="name" valueKey="id" placeholder="Search Designations…" />
        )}

        {/* Rich text editor + pending attachments inside same visual box */}
        <div>
          <div className="feed-modal-msg-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{postType === 'poll' ? 'Add a note (optional)' : 'Message'}</span>
            {postType === 'poll' && (
              <span className={noteLen > 1000 ? 'feed-char-count feed-char-count--over' : 'feed-char-count'}>{noteLen} / 1000</span>
            )}
          </div>
          <div className="feed-editor-with-attachments">
            <RichEditor
              ref={richEditorRef}
              appId={appId}
              users={members}
              tasks={tasks}
              onChange={setContent}
              noBorder
              onAttach={() => { richEditorRef.current?.saveRange(); feedFileRef.current?.click(); }}
              onFilePaste={(files) => addFiles(files)}
              frameStyle={frameStyle}
              onFrameChange={setFrameStyle}
              maxLength={postType === 'poll' ? 1000 : 100000}
              richPaste={postType !== 'poll'}
              placeholder={postType === 'announcement'
                ? 'Write an announcement for the team…'
                : postType === 'poll'
                  ? 'Add context for your poll…'
                  : 'Share an update, idea, or something that matters to you…'}
            />
          </div>
          {fileErrors.length > 0 && (
            <div className="att-pending-errors" style={{ marginTop: 6 }}>
              {fileErrors.map((e, i) => <div key={i} className="att-pending-error">{e}</div>)}
            </div>
          )}
        </div>

        <input
          ref={feedFileRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          style={{ display: 'none' }}
        />

        {err && <div className="feed-modal-error">{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} className="feed-btn-cancel">Cancel</button>
          <button onClick={submit} disabled={saving || uploading || (postType === 'poll' && noteLen > 1000)} className="btn-primary">
            {saving || uploading ? 'Posting…' : postType === 'poll' ? '📊 Post Poll' : postType === 'announcement' ? '📢 Post Announcement' : '💬 Post'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit Post Modal ──────────────────────────────────────────────────────────

function EditPostModal({ appId, post, me, members, departments, roles, onDone, onClose }) {
  const api = useApi();
  const isPoll = post.post_type === 'poll';
  const [content,      setContent]      = useState(post.content || '');
  const [editAtts,     setEditAtts]     = useState(post.attachments || []);
  const [pollQuestion, setPollQuestion] = useState(post.poll?.question || '');
  const [pollOptions,  setPollOptions]  = useState(
    isPoll ? (post.poll?.options || []).map(o => ({ id: o.id, text: o.option_text })) : []
  );
  const [pollMulti,    setPollMulti]    = useState(!!post.poll?.allow_multiple);
  const [fileErrors,   setFileErrors]   = useState([]);
  const [frameStyle,   setFrameStyle]   = useState(post.frame_style || null);
  const [saving,       setSaving]       = useState(false);
  const [err,          setErr]          = useState('');
  const richEditorRef = useRef(null);
  const feedFileRef   = useRef(null);
  const { uploadFiles } = useSpacesUpload(appId, 'feed');

  const initialAudienceIds = JSON.parse(post.audience_ids || '[]').map(Number);
  const [audienceType,  setAudienceType]  = useState(post.audience_type || 'all');
  const [selectedUsers, setSelectedUsers] = useState(post.audience_type === 'users' ? initialAudienceIds : []);
  const [selectedDepts, setSelectedDepts] = useState(post.audience_type === 'departments' ? initialAudienceIds : []);
  const [selectedRoles, setSelectedRoles] = useState(post.audience_type === 'roles' ? initialAudienceIds : []);

  const audienceIds = () => {
    if (audienceType === 'users')       return selectedUsers;
    if (audienceType === 'departments') return selectedDepts;
    if (audienceType === 'roles')       return selectedRoles;
    return [];
  };
  const audienceValid = () => audienceType === 'all' || audienceIds().length > 0;
  const audienceOptions = [
    { value: 'all',         label: '🌐 Everyone',         hint: 'Visible to all workspace members' },
    { value: 'users',       label: '👤 Specific People',  hint: 'Choose one or more members' },
  ];
  const memberItems = members.filter(m => m.id !== me).map(m => ({ ...m, name: uname(m) }));
  const deptItems    = departments.map(d => ({ ...d }));
  const roleItems    = roles.map(r => ({ ...r, name: r.title }));

  const addFiles = (incoming) => {
    const { valid, errors } = validateFiles(incoming);
    setFileErrors(errors);
    // Same as the create flow: images embed inline at the cursor so the
    // compose view matches what actually gets posted; non-image files
    // stay as regular attachments below.
    if (valid.length) richEditorRef.current?.insertFiles(valid);
  };

  useEffect(() => {
    const input = feedFileRef.current;
    if (!input) return;
    const handler = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      e.target.value = '';
    };
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, []);

  const updatePollOption = (i, val) => setPollOptions(prev => prev.map((o, idx) => idx === i ? { ...o, text: val } : o));
  const addPollOption    = () => setPollOptions(prev => prev.length < 10 ? [...prev, { id: null, text: '' }] : prev);
  const removePollOption = (i) => setPollOptions(prev => prev.filter((_, idx) => idx !== i));

  const noteLen = content.replace(/<[^>]+>/g, '').length;

  const submit = async () => {
    setErr('');
    // Pull any pending attach-button/paste files out of the editor before
    // reading its final value — same two-step as the create flow.
    const inlineFiles = richEditorRef.current?.extractFiles() || [];
    const cleanedContent = (richEditorRef.current?.getValue() ?? content).trim();

    if (!audienceValid()) return setErr('Please select at least one recipient');

    if (isPoll) {
      const question = pollQuestion.trim();
      const opts = pollOptions.map(o => ({ id: o.id, option_text: o.text.trim() })).filter(o => o.option_text);
      if (!question) return setErr('Add a poll question');
      if (opts.length < 2) return setErr('Add at least 2 options');
      const polledNoteLen = cleanedContent.replace(/<[^>]+>/g, '').length;
      if (polledNoteLen > 1000) return setErr('Note must be under 1000 characters');
      setSaving(true);
      try {
        const newAtts = inlineFiles.length > 0 ? await uploadFiles(inlineFiles) : [];
        await api.updatePost(appId, post.id, {
          content:       cleanedContent,
          poll_question: question,
          poll_options:  opts,
          poll_multi:    pollMulti,
          audience_type: audienceType,
          audience_ids:  audienceIds(),
          attachments:   [...editAtts, ...newAtts],
          frame_style:   frameStyle,
        });
        onDone();
      } catch (e) {
        setErr(e.response?.data?.error || 'Failed to update poll');
      } finally { setSaving(false); }
      return;
    }
    // Checking the raw HTML string (tags included) rather than its stripped
    // text meant a post left visually empty by select-all-delete — which
    // browsers usually leave as an empty <p></p>/<div><br></div>, not a true
    // '' string — read as "has content" and saved anyway.
    const hasMedia = /<img\b|<video\b/i.test(cleanedContent);
    if (!cleanedContent.replace(/<[^>]+>/g, '').trim() && !hasMedia && editAtts.length === 0 && inlineFiles.length === 0) {
      return setErr('Write something or attach a file');
    }
    setSaving(true);
    try {
      const newAtts = inlineFiles.length > 0 ? await uploadFiles(inlineFiles) : [];
      await api.updatePost(appId, post.id, {
        content: cleanedContent,
        audience_type: audienceType,
        audience_ids:  audienceIds(),
        attachments: [...editAtts, ...newAtts],
        frame_style: frameStyle,
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to update post');
    } finally { setSaving(false); }
  };

  return (
    <Modal open title={isPoll ? 'Edit Poll' : 'Edit Post'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {isPoll && (
          <>
            <div>
              <div className="feed-modal-msg-label">Question <span className="feed-modal-required">*</span></div>
              <input
                type="text"
                className="feed-poll-question-input"
                value={pollQuestion}
                onChange={e => setPollQuestion(e.target.value)}
                placeholder="Ask your team something…"
                maxLength={300}
              />
            </div>
            <div>
              <div className="feed-modal-msg-label">Options</div>
              <div className="feed-poll-options-edit">
                {pollOptions.map((opt, i) => (
                  <div key={opt.id ?? `new-${i}`} className="feed-poll-option-edit-row">
                    <input
                      type="text"
                      value={opt.text}
                      onChange={e => updatePollOption(i, e.target.value)}
                      placeholder={`Option ${i + 1}`}
                      maxLength={200}
                    />
                    {i >= 2 && (
                      <button type="button" className="feed-ghost-btn feed-ghost-btn--danger feed-poll-option-remove" onClick={() => removePollOption(i)} title="Remove option">✕</button>
                    )}
                  </div>
                ))}
              </div>
              {pollOptions.length < 10 && (
                <button type="button" className="feed-poll-add-option-btn" onClick={addPollOption}>+ Add option</button>
              )}
            </div>
            <label className="feed-poll-multi-toggle">
              <input type="checkbox" checked={pollMulti} onChange={e => setPollMulti(e.target.checked)} />
              Allow selecting multiple options
            </label>
          </>
        )}

        {/* Audience selector */}
        <div>
          <div className="feed-modal-section-label">Who can see this?</div>
          <div className="feed-modal-audience-opts">
            {audienceOptions.map(opt => (
              <label key={opt.value} className={`feed-modal-audience-opt${audienceType === opt.value ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="editAudienceType"
                  value={opt.value}
                  checked={audienceType === opt.value}
                  onChange={() => setAudienceType(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {audienceType !== 'all' && (
            <div className="feed-modal-audience-hint">
              {audienceOptions.find(o => o.value === audienceType)?.hint}
            </div>
          )}
        </div>
        {audienceType === 'users' && (
          <MultiSelectList items={memberItems} selected={selectedUsers} onChange={setSelectedUsers} labelKey="name" valueKey="id" placeholder="Search Members…" />
        )}
        {audienceType === 'departments' && (
          <MultiSelectList items={deptItems} selected={selectedDepts} onChange={setSelectedDepts} labelKey="name" valueKey="id" placeholder="Search Departments…" />
        )}
        {audienceType === 'roles' && (
          <MultiSelectList items={roleItems} selected={selectedRoles} onChange={setSelectedRoles} labelKey="name" valueKey="id" placeholder="Search Designations…" />
        )}

        {isPoll && (
          <div className="feed-modal-msg-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Add a note (optional)</span>
            <span className={noteLen > 1000 ? 'feed-char-count feed-char-count--over' : 'feed-char-count'}>{noteLen} / 1000</span>
          </div>
        )}
        <div className="feed-editor-with-attachments">
          <RichEditor
            ref={richEditorRef}
            appId={appId}
            users={[]}
            tasks={[]}
            initialValue={post.content}
            onChange={setContent}
            noBorder
            onAttach={() => { richEditorRef.current?.saveRange(); feedFileRef.current?.click(); }}
            onFilePaste={(files) => addFiles(files)}
            frameStyle={frameStyle}
            onFrameChange={setFrameStyle}
            maxLength={isPoll ? 1000 : 100000}
            richPaste={!isPoll}
            placeholder={isPoll ? 'Add context for your poll…' : 'Edit your post…'}
          />
          {editAtts.length > 0 && (
            <div className="feed-editor-existing-atts">
              <AttachmentDisplay
                attachments={editAtts}
                onDelete={(a) => setEditAtts(prev => prev.filter(x => x.id !== a.id))}
              />
            </div>
          )}
          {fileErrors.length > 0 && (
            <div className="att-pending-errors" style={{ marginTop: 6 }}>
              {fileErrors.map((e, i) => <div key={i} className="att-pending-error">{e}</div>)}
            </div>
          )}
        </div>
        <input
          ref={feedFileRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          style={{ display: 'none' }}
        />
        {err && <div className="feed-modal-error">{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} className="feed-btn-cancel">Cancel</button>
          <button onClick={submit} disabled={saving || (isPoll && noteLen > 1000)} className="btn-primary">
            {saving ? 'Saving…' : '💾 Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Give Appreciation Modal ──────────────────────────────────────────────────

function AppreciationModal({ appId, me, members, editing, onDone, onClose }) {
  const api = useApi();
  const isEdit = !!editing;

  const initialToIds = isEdit
    ? ((editing.to_users?.length ? editing.to_users.map(u => u.id) : (editing.to_user ? [editing.to_user.id] : [])))
    : [];

  const [toUserIds,  setToUserIds]  = useState(initialToIds);
  const [message,    setMessage]    = useState(isEdit ? editing.message : '');
  const [badge,      setBadge]      = useState(isEdit ? (editing.badge || '') : '');
  const [editAtts,   setEditAtts]   = useState(isEdit ? (editing.attachments || []) : []);
  const [fileErrors, setFileErrors] = useState([]);
  const [frameStyle, setFrameStyle] = useState(isEdit ? (editing.frame_style || null) : null);
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');
  const richEditorRef = useRef(null);
  const feedFileRef   = useRef(null);
  const { uploadFiles } = useSpacesUpload(appId, 'appreciation');

  // You can't appreciate yourself — leave yourself out of the picker
  // entirely rather than letting it be selected and rejected later.
  const memberItems = members.filter(m => m.id !== me).map(m => ({ id: m.id, name: uname(m) }));

  const addFiles = (incoming) => {
    const { valid, errors } = validateFiles(incoming);
    setFileErrors(errors);
    if (valid.length) richEditorRef.current?.insertFiles(valid);
  };

  useEffect(() => {
    const input = feedFileRef.current;
    if (!input) return;
    const handler = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      e.target.value = '';
    };
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, []);

  const submit = async () => {
    if (!toUserIds.length) return setErr('Please select at least one recipient');
    const inlineFiles = richEditorRef.current?.extractFiles() || [];
    const cleanedMessage = richEditorRef.current?.getValue() || message;
    const hasMedia = /<img\b|<video\b/i.test(cleanedMessage);
    if (!cleanedMessage.replace(/<[^>]+>/g, '').trim() && !hasMedia && inlineFiles.length === 0 && editAtts.length === 0) {
      return setErr('Please write a message');
    }
    setSaving(true); setErr('');
    try {
      const newAtts = inlineFiles.length > 0 ? await uploadFiles(inlineFiles) : [];
      const attachments = [...editAtts, ...newAtts];
      const payload = { to_user_ids: toUserIds.map(Number), message: cleanedMessage, badge: badge || null, attachments, frame_style: frameStyle };
      if (isEdit) await api.updateAppreciation(appId, editing.id, payload);
      else await api.giveAppreciation(appId, payload);
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to submit');
    } finally { setSaving(false); }
  };

  return (
    <Modal open title={isEdit ? 'Edit Appreciation 🙌' : 'Give Appreciation 🙌'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="filter-group" style={{ marginBottom: 0 }}>
          <label>Who are you appreciating? *</label>
          <MultiSelectList items={memberItems} selected={toUserIds} onChange={setToUserIds} labelKey="name" valueKey="id" placeholder="Search members…" />
        </div>

        <div className="filter-group" style={{ marginBottom: 0 }}>
          <label>Badge (optional)</label>
          <select value={badge} onChange={e => setBadge(e.target.value)}>
            <option value="">— No badge —</option>
            {Object.entries(BADGE_META).map(([k, v]) => (
              <option key={k} value={k}>{v.emoji} {v.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group" style={{ marginBottom: 0 }}>
          <label>Your message *</label>
          <div className="feed-editor-with-attachments">
            <RichEditor
              ref={richEditorRef}
              appId={appId}
              users={members}
              initialValue={isEdit ? editing.message : undefined}
              onChange={setMessage}
              noBorder
              onAttach={() => { richEditorRef.current?.saveRange(); feedFileRef.current?.click(); }}
              onFilePaste={(files) => addFiles(files)}
              frameStyle={frameStyle}
              onFrameChange={setFrameStyle}
              maxLength={100000}
              placeholder="What did they do that deserves recognition?"
            />
            {editAtts.length > 0 && (
              <div className="feed-editor-existing-atts">
                <AttachmentDisplay
                  attachments={editAtts}
                  onDelete={(a) => setEditAtts(prev => prev.filter(x => x.id !== a.id))}
                />
              </div>
            )}
          </div>
          {fileErrors.length > 0 && (
            <div className="att-pending-errors" style={{ marginTop: 6 }}>
              {fileErrors.map((e, i) => <div key={i} className="att-pending-error">{e}</div>)}
            </div>
          )}
        </div>

        <input
          ref={feedFileRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          style={{ display: 'none' }}
        />

        {err && <div className="feed-modal-error">{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} className="feed-btn-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary feed-btn-primary--warn">
            {saving ? 'Saving…' : isEdit ? '💾 Save Changes' : '🙌 Give Appreciation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Give Feedback Modal ──────────────────────────────────────────────────────

function FeedbackModal({ appId, me, members, cycles, editing, onDone, onClose }) {
  const api = useApi();
  const isEdit = !!editing;

  const activeCycle = findActiveCycle(cycles);
  const initialToIds = isEdit
    ? ((editing.to_users?.length ? editing.to_users.map(u => u.id) : (editing.to_user ? [editing.to_user.id] : [])))
    : [];

  const [toUserIds,    setToUserIds]    = useState(initialToIds);
  const [feedbackText, setFeedbackText] = useState(isEdit ? editing.feedback_text : '');
  const [isAnon,       setIsAnon]       = useState(isEdit ? !!editing.is_anonymous : false);
  const [cycleId,      setCycleId]      = useState(isEdit ? String(editing.cycle_id || '') : (activeCycle ? String(activeCycle.id) : ''));
  const [editAtts,     setEditAtts]     = useState(isEdit ? (editing.attachments || []) : []);
  const [fileErrors,   setFileErrors]   = useState([]);
  const [frameStyle,   setFrameStyle]   = useState(isEdit ? (editing.frame_style || null) : null);
  const [saving,       setSaving]       = useState(false);
  const [err,          setErr]          = useState('');
  const richEditorRef = useRef(null);
  const feedFileRef   = useRef(null);
  const { uploadFiles } = useSpacesUpload(appId, 'feedback');

  const memberItems = members.filter(m => m.id !== me).map(m => ({ id: m.id, name: uname(m) }));

  const addFiles = (incoming) => {
    const { valid, errors } = validateFiles(incoming);
    setFileErrors(errors);
    if (valid.length) richEditorRef.current?.insertFiles(valid);
  };

  useEffect(() => {
    const input = feedFileRef.current;
    if (!input) return;
    const handler = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      e.target.value = '';
    };
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, []);

  const submit = async () => {
    if (!toUserIds.length) return setErr('Please select at least one recipient');
    const inlineFiles = richEditorRef.current?.extractFiles() || [];
    const cleanedText = richEditorRef.current?.getValue() || feedbackText;
    const hasMedia = /<img\b|<video\b/i.test(cleanedText);
    if (!cleanedText.replace(/<[^>]+>/g, '').trim() && !hasMedia && inlineFiles.length === 0 && editAtts.length === 0) {
      return setErr('Please write your feedback');
    }
    setSaving(true); setErr('');
    try {
      const newAtts = inlineFiles.length > 0 ? await uploadFiles(inlineFiles) : [];
      const payload = {
        to_user_ids: toUserIds.map(Number),
        feedback_text: cleanedText,
        is_anonymous: isAnon,
        cycle_id: cycleId ? parseInt(cycleId) : null,
        attachments: [...editAtts, ...newAtts],
        frame_style: frameStyle,
      };
      if (isEdit) await api.updateFeedback(appId, editing.id, payload);
      else await api.giveFeedback(appId, payload);
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to submit');
    } finally { setSaving(false); }
  };

  return (
    <Modal open title={isEdit ? 'Edit Feedback 💬' : 'Give Feedback 💬'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="filter-group" style={{ marginBottom: 0 }}>
          <label>Who is this feedback for? *</label>
          <MultiSelectList items={memberItems} selected={toUserIds} onChange={setToUserIds} labelKey="name" valueKey="id" placeholder="Search members…" />
        </div>

        <div className="filter-group" style={{ marginBottom: 0 }}>
          <label>Review Period</label>
          {cycles.length === 0
            ? <span style={{ fontSize: 13, color: 'var(--warn)' }}>No review cycles found</span>
            : (
              <>
                <select value={cycleId} onChange={e => setCycleId(e.target.value)}>
                  <option value="">— Not linked —</option>
                  {cycles.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.cycle_name} ({c.year}){activeCycle?.id === c.id ? ' ★ Active' : ''}
                    </option>
                  ))}
                </select>
                {activeCycle && (
                  <div style={{ fontSize: 11, color: 'var(--pos)', marginTop: 3 }}>
                    ★ Auto-selected: <strong>{activeCycle.cycle_name}</strong> is the active review period
                  </div>
                )}
              </>
            )
          }
        </div>

        <div className="filter-group" style={{ marginBottom: 0 }}>
          <label>Your feedback *</label>
          <div className="feed-editor-with-attachments">
            <RichEditor
              ref={richEditorRef}
              appId={appId}
              users={members}
              initialValue={isEdit ? editing.feedback_text : undefined}
              onChange={setFeedbackText}
              noBorder
              onAttach={() => { richEditorRef.current?.saveRange(); feedFileRef.current?.click(); }}
              onFilePaste={(files) => addFiles(files)}
              frameStyle={frameStyle}
              onFrameChange={setFrameStyle}
              maxLength={100000}
              placeholder="Be specific and constructive. Focus on behaviours and impact."
            />
            {editAtts.length > 0 && (
              <div className="feed-editor-existing-atts">
                <AttachmentDisplay
                  attachments={editAtts}
                  onDelete={(a) => setEditAtts(prev => prev.filter(x => x.id !== a.id))}
                />
              </div>
            )}
          </div>
          {fileErrors.length > 0 && (
            <div className="att-pending-errors" style={{ marginTop: 6 }}>
              {fileErrors.map((e, i) => <div key={i} className="att-pending-error">{e}</div>)}
            </div>
          )}
        </div>

        <input
          ref={feedFileRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          style={{ display: 'none' }}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={isAnon} onChange={e => setIsAnon(e.target.checked)} />
          Submit anonymously (your name won't be shown to the recipient)
        </label>

        {err && <div className="feed-modal-error">{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} className="feed-btn-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : isEdit ? '💾 Save Changes' : '💬 Submit Feedback'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Appreciation card ────────────────────────────────────────────────────────

function AppreciationCard({ a, direction, highlighted, appId, me, onEdit, onDeleted }) {
  const api = useApi();
  const badge = a.badge ? BADGE_META[a.badge] : null;

  const ageMs = Date.now() - new Date(a.created_at).getTime();
  const canEdit   = direction === 'given' && a.from_user_id === me && ageMs < 15 * 60 * 1000;
  const canDelete = direction === 'given';

  const handleDelete = async () => {
    if (!window.confirm('Delete this appreciation? This will remove it for all recipients.')) return;
    try {
      await api.deleteAppreciation(appId, a.id);
      onDeleted && onDeleted(a.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete appreciation');
    }
  };

  return (
    <div
      className={`feed-appr-card${highlighted ? ' feed-appr-card--highlight' : ''}`}
      data-highlight={highlighted ? 'true' : undefined}
    >
      {badge && <div className="feed-appr-card-icon">{badge.emoji}</div>}
      <div className="feed-appr-card-content">
        <div className="feed-appr-card-from" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span>
            {direction === 'received'
              ? <>From <strong>{uname(a.from_user)}</strong></>
              : <>To <strong><NamesWithMore names={
                  (a.to_users && a.to_users.length)
                    ? a.to_users.map(u => uname(u))
                    : (a.to_user ? [uname(a.to_user)] : [])
                } inline /></strong></>}
            {badge && <span className="feed-appr-card-badge-label">[{badge.label}]</span>}
          </span>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {canEdit && (
              <button onClick={() => onEdit?.(a)} className="feed-ghost-btn" title="Edit (within 15 min)" style={{ fontSize: 13 }}>✏️</button>
            )}
            {canDelete && (
              <button onClick={handleDelete} className="feed-ghost-btn feed-ghost-btn--danger" title="Delete appreciation" style={{ fontSize: 13 }}>🗑</button>
            )}
          </div>
        </div>

        <div className="feed-appr-card-msg"><RichContent html={a.message} /></div>
        {a.attachments?.length > 0 && <AttachmentDisplay attachments={a.attachments} />}

        <div className="feed-appr-card-date">{fmtDate(a.created_at)}</div>
      </div>
    </div>
  );
}

// ─── Feedback card ────────────────────────────────────────────────────────────

function FeedbackCard({ fb, direction, appId, me, onEdit, onDeleted }) {
  const api = useApi();

  const ageMs = Date.now() - new Date(fb.created_at).getTime();
  const canEdit = direction === 'given' && fb.from_user_id === me && ageMs < 15 * 60 * 1000;
  const canDelete = direction === 'given';

  const handleDelete = async () => {
    if (!window.confirm('Delete this feedback? This will remove it for all recipients.')) return;
    try {
      await api.deleteFeedback(appId, fb.id);
      onDeleted && onDeleted(fb.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete feedback');
    }
  };

  return (
    <div className="feed-feedback-card">
      <div className="feed-feedback-from" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {direction === 'received'
            ? fb.is_anonymous
              ? <em style={{ color: 'var(--text-muted)' }}>Anonymous feedback</em>
              : <><strong>{uname(fb.from_user)}</strong> gave you feedback</>
            : <>You gave feedback to <strong><NamesWithMore names={
                (fb.to_users && fb.to_users.length)
                  ? fb.to_users.map(u => uname(u))
                  : (fb.to_user ? [uname(fb.to_user)] : [])
              } inline /></strong></>}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {canEdit && (
            <button onClick={() => onEdit?.(fb)} className="feed-ghost-btn" title="Edit (within 15 min)" style={{ fontSize: 13 }}>✏️</button>
          )}
          {canDelete && (
            <button onClick={handleDelete} className="feed-ghost-btn feed-ghost-btn--danger" title="Delete feedback" style={{ fontSize: 13 }}>🗑</button>
          )}
        </div>
      </div>

      <div className="feed-feedback-body">
        <RichContent html={fb.feedback_text} />
        {fb.attachments?.length > 0 && <AttachmentDisplay attachments={fb.attachments} />}
      </div>

      <div className="feed-feedback-date">{fmtDate(fb.created_at)}</div>
    </div>
  );
}

// ─── Feed Panel ───────────────────────────────────────────────────────────────

function FeedPanel({ appId, me, isAdmin, members, departments, roles, cycles, onShowAppreciate, onShowFeedback, onShowPost, onShowPoll, reloadKey, searchQuery, preFilter, monthFilter, hashFilter, onClearHash, onRemoveHash }) {
  const api = useApi();
  const [posts,       setPosts]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [err,         setErr]         = useState('');
  const [filter,      setFilter]      = useState(preFilter || 'all');
  const [monthOnly,   setMonthOnly]   = useState(monthFilter || null);
  const [editingPost, setEditingPost] = useState(null);
  const [editingAppr, setEditingAppr] = useState(null);
  const [editingFb,   setEditingFb]   = useState(null);

  // preFilter/monthFilter can change without this component remounting (e.g.
  // navigating from the "Top Appreciations" sidebar card into the Feed tab
  // pre-filtered), so keep the dropdown in sync instead of only reading it
  // on initial mount.
  useEffect(() => { if (preFilter) setFilter(preFilter); }, [preFilter]);
  useEffect(() => { setMonthOnly(monthFilter || null); }, [monthFilter]);

  // Manually touching the dropdown means the user is now doing their own
  // general browsing — drop the "this month only" scope that View All
  // arrived with, so switching to e.g. "Posts" shows all posts, not just
  // this month's.
  const handleFilterChange = (value) => { setFilter(value); setMonthOnly(null); };

  // type/month are applied server-side (see backend) so `total` — and the
  // "Load more (N remaining)" count derived from it — reflects the filtered
  // set instead of the whole feed's count.
  // "appreciation"/"feedback" swap in their own dedicated Received/Given
  // panels below instead of the post list, so there's no post feed to fetch.
  const load = useCallback((silent = false) => {
    if (filter === 'appreciation' || filter === 'feedback') { setLoading(false); return; }
    if (!silent) setLoading(true);
    const params = { limit: 30, offset: 0 };
    if (filter !== 'all') params.type = filter;
    if (monthOnly) params.month = monthOnly;
    api.getFeed(appId, params)
      .then(d => { setPosts(d.items || []); setTotal(d.total || 0); })
      .catch(() => { if (!silent) setErr('Failed to load feed'); })
      .finally(() => setLoading(false));
  }, [api, appId, filter, monthOnly]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const loadMore = () => {
    setLoadingMore(true);
    const isHashActive = hashFilter && hashFilter.size > 0;
    const limit = isHashActive ? (total - posts.length) : 20;
    const params = { limit, offset: posts.length };
    if (filter !== 'all') params.type = filter;
    if (monthOnly) params.month = monthOnly;
    api.getFeed(appId, params)
      .then(d => {
        setPosts(prev => [...prev, ...(d.items || [])]);
        setTotal(d.total || 0);
        if (isHashActive) onClearHash();
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    setPosts(prev => prev.filter(p => p.id !== id));
    try { await api.deletePost(appId, id); load(true); }
    catch { setErr('Failed to delete'); }
  };
  const handlePin = async (id) => {
    setPosts(prev => prev.map(p => p.id === id ? { ...p, pinned: !p.pinned } : p));
    try { await api.pinPost(appId, id); load(true); }
    catch { setErr('Failed to pin'); }
  };
  const handleReact = async (id, emoji) => {
    setPosts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const myReactions = p.my_reactions || [];
      const alreadyReacted = myReactions.includes(emoji);
      const newMyReactions = alreadyReacted
        ? myReactions.filter(e => e !== emoji)
        : [...myReactions, emoji];
      const newReactions = alreadyReacted
        ? (p.reactions || []).map(r => r.emoji === emoji ? { ...r, count: Math.max(0, Number(r.count) - 1) } : r).filter(r => r.count > 0)
        : (p.reactions || []).some(r => r.emoji === emoji)
          ? (p.reactions || []).map(r => r.emoji === emoji ? { ...r, count: Number(r.count) + 1 } : r)
          : [...(p.reactions || []), { emoji, count: 1 }];
      return { ...p, my_reactions: newMyReactions, reactions: newReactions };
    }));
    try { await api.reactToPost(appId, id, emoji); }
    catch { /* silent */ }
  };

  const handleVote = async (id, optionIds) => {
    try {
      const { poll } = await api.votePoll(appId, id, optionIds);
      setPosts(prev => prev.map(p => p.id === id ? { ...p, poll } : p));
    } catch { setErr('Failed to vote'); }
  };

  if (loading) return <div className="loading">Loading feed…</div>;

  // Appreciation/feedback posts within the merged "All Activities" list edit
  // through their own create-style modals (recipients, badge/cycle, rich
  // message) rather than the plain post editor — same as their dedicated panels.
  const handleEdit = (post) => {
    if (post.post_type === 'appreciation' && post.appreciation) setEditingAppr({ ...post.appreciation, frame_style: post.frame_style });
    else if (post.post_type === 'feedback' && post.feedback) setEditingFb({ ...post.feedback, frame_style: post.frame_style });
    else setEditingPost(post);
  };

  const q = (searchQuery || '').toLowerCase().trim();
  const matchesQuery = (p) => {
    if (!q) return true;
    const text = (p.content || '').replace(/<[^>]+>/g, '').toLowerCase();
    const author = uname(p.author).toLowerCase();
    const apprMsg = (p.appreciation?.message || '').toLowerCase();
    const apprFrom = uname(p.appreciation?.from_user).toLowerCase();
    const apprTo = uname(p.appreciation?.to_user).toLowerCase();
    const fbText = (p.feedback?.feedback_text || '').replace(/<[^>]+>/g, '').toLowerCase();
    const fbFrom = p.feedback?.is_anonymous ? '' : uname(p.feedback?.from_user).toLowerCase();
    const fbTo   = uname(p.feedback?.to_user).toLowerCase();
    const pollQ = (p.poll?.question || '').toLowerCase();
    return text.includes(q) || author.includes(q) || apprMsg.includes(q) || apprFrom.includes(q) || apprTo.includes(q)
      || fbText.includes(q) || fbFrom.includes(q) || fbTo.includes(q) || pollQ.includes(q);
  };

  const matchesHash = (p) => {
    if (!hashFilter || hashFilter.size === 0) return true;
    const text = (p.content || '').replace(/<[^>]+>/g, '');
    const tags = (text.match(/#\w+/g) || []).map(t => t.toLowerCase());
    return [...hashFilter].some(f => tags.includes(f.toLowerCase()));
  };

  // Type filtering now happens server-side (see load/loadMore) so `total`
  // stays accurate for "Load more"; only search/hashtag filtering remains
  // client-side since those are local, instant-feedback conveniences.
  const visiblePosts = posts.filter(matchesQuery).filter(matchesHash);

  const meUser = members.find(m => m.id === me);
  const meFirstName = meUser ? (meUser.first_name || uname(meUser).split(' ')[0] || '') : '';
  const meInitial = meFirstName ? meFirstName.charAt(0).toUpperCase() : '?';
  const meAvatarColor = AVATAR_COLORS[(me || 0) % AVATAR_COLORS.length];

  return (
    <div>
      {err && <div className="error-message">{err}</div>}

      {hashFilter && hashFilter.size > 0 && (
        <div className="feed-hash-filter-banner">
          <span className="feed-hash-filter-banner-label">Filtered by:</span>
          {[...hashFilter].map(t => (
            <span key={t} className="feed-hash-filter-tag">
              {t}
              <button className="feed-hash-filter-tag-remove" onClick={() => onRemoveHash(t)} title={`Remove ${t}`}>×</button>
            </span>
          ))}
          <button onClick={onClearHash} className="feed-hash-filter-clear">✕ Clear all</button>
        </div>
      )}

      {/* Compose bar */}
      <div className="feed-compose-bar">
        <div className="feed-compose-avatar" style={{ background: meUser?.photo_url ? 'transparent' : meAvatarColor }}>
          {meUser?.photo_url
            ? <img src={meUser.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : meInitial}
        </div>
        <button className="feed-compose-placeholder" onClick={onShowPost}>
          {meFirstName ? `What's on your mind, ${meFirstName}?` : "What's on your mind?"}
        </button>
        <div className="feed-compose-actions">
          <button className="feed-compose-action-btn" onClick={onShowPost}>✏️ <span>Post</span></button>
          <button className="feed-compose-action-btn feed-compose-action-btn--poll" onClick={onShowPoll}>📊 <span>Poll</span></button>
          <button className="feed-compose-action-btn feed-compose-action-btn--warn" onClick={onShowAppreciate}>🤝 <span>Appreciate</span></button>
          <button className="feed-compose-action-btn feed-compose-action-btn--info" onClick={onShowFeedback}>💬 <span>Feedback</span></button>
        </div>
      </div>
      {/* Always visible — including when arriving via "View All" from the
          Top Appreciations card — so it's clear which filter is active and
          the user can change it (e.g. back to "All Activities") from here. */}
      <div className="feed-filter-row">
        <SearchableSelect
          value={filter}
          onChange={handleFilterChange}
          options={[
            { value: 'all', label: 'All Activities' },
            { value: 'post', label: 'Posts' },
            { value: 'poll', label: 'Polls' },
            { value: 'appreciation', label: 'Appreciations' },
            { value: 'feedback', label: 'Feedback' },
          ]}
          showAll={false}
          wrapStyle={{ marginLeft: 'auto', width: 'auto' }}
          dropdownWidth={180}
        />
      </div>

      {filter === 'appreciation' && (
        <AppreciationsPanel appId={appId} me={me} members={members} onShowAppreciate={onShowAppreciate} reloadKey={reloadKey} searchQuery={searchQuery} monthOnly={monthOnly} />
      )}
      {filter === 'feedback' && (
        <FeedbackPanel appId={appId} me={me} members={members} cycles={cycles} onShowFeedback={onShowFeedback} reloadKey={reloadKey} searchQuery={searchQuery} />
      )}
      {filter !== 'appreciation' && filter !== 'feedback' && (
        <>
          {visiblePosts.length === 0
            ? <div className="feed-empty">
                <div className="feed-empty-icon">📭</div>
                <div className="feed-empty-text">No posts yet. Be the first to share something!</div>
              </div>
            : visiblePosts.map(p => (
                <FeedCard
                  key={p.id}
                  post={p}
                  me={me}
                  isAdmin={isAdmin}
                  members={members}
                  departments={departments}
                  roles={roles}
                  appId={appId}
                  onDelete={handleDelete}
                  onPin={handlePin}
                  onReact={handleReact}
                  onEdit={handleEdit}
                  onVote={handleVote}
                />
              ))
          }
          {posts.length < total && (
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="feed-load-more-btn"
              >
                {loadingMore
                  ? 'Loading…'
                  : (hashFilter && hashFilter.size > 0)
                    ? `Load All (${total - posts.length} more)`
                    : `Load more (${total - posts.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}
      {editingPost && (
        <EditPostModal
          appId={appId}
          post={editingPost}
          me={me}
          members={members} departments={departments} roles={roles}
          onDone={() => { setEditingPost(null); load(true); }}
          onClose={() => setEditingPost(null)}
        />
      )}
      {editingAppr && (
        <AppreciationModal
          appId={appId} me={me} members={members}
          editing={editingAppr}
          onDone={() => { setEditingAppr(null); load(true); }}
          onClose={() => setEditingAppr(null)}
        />
      )}
      {editingFb && (
        <FeedbackModal
          appId={appId} me={me} members={members} cycles={cycles}
          editing={editingFb}
          onDone={() => { setEditingFb(null); load(true); }}
          onClose={() => setEditingFb(null)}
        />
      )}
    </div>
  );
}

// ─── Appreciations Panel ──────────────────────────────────────────────────────

function AppreciationsPanel({ appId, me, members, reloadKey, searchQuery, highlightUserId, monthOnly }) {
  const api = useApi();
  const [received,     setReceived]     = useState([]);
  const [given,        setGiven]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState('');
  const [visibleCount, setVisibleCount] = useState(30);
  const [localKey,     setLocalKey]     = useState(0);
  const [editingItem,  setEditingItem]  = useState(null);
  const listRef = useRef(null);

  const reload = () => setLocalKey(k => k + 1);

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    api.getAppreciationsForUser(appId, me)
      .then(d => { setReceived(d.received || []); setGiven(d.given || []); })
      .catch(() => setErr('Failed to load'))
      .finally(() => setLoading(false));
  }, [appId, me, reloadKey, localKey]);

  useEffect(() => {
    if (!highlightUserId) return;
    setTimeout(() => {
      const el = listRef.current?.querySelector('[data-highlight="true"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 120);
  }, [highlightUserId, given, received]);

  if (loading) return <div className="loading">Loading…</div>;
  const q = (searchQuery || '').toLowerCase().trim();
  // Single combined feed, newest first — no Received/Given tabs to switch between.
  let allList = [
    ...received.map(a => ({ ...a, _direction: 'received' })),
    ...given.map(a => ({ ...a, _direction: 'given' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  if (monthOnly === 'current') {
    const monthStart = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
    allList = allList.filter(a => new Date(a.created_at) >= monthStart);
  }
  const filtered = q
    ? allList.filter(a =>
        uname(a.from_user).toLowerCase().includes(q) ||
        uname(a.to_user).toLowerCase().includes(q) ||
        (a.message || '').toLowerCase().includes(q) ||
        (BADGE_META[a.badge]?.label || '').toLowerCase().includes(q)
      )
    : allList;
  const list = filtered.slice(0, visibleCount);

  return (
    <div ref={listRef}>
      {err && <div className="error-message">{err}</div>}
      {list.length === 0
        ? <div className="feed-empty feed-empty--sm">
            <div className="feed-empty-icon">🙌</div>
            <div className="feed-empty-text">{q ? `No results for "${q}"` : 'No appreciations yet.'}</div>
          </div>
        : list.map(a => {
            const isHighlighted = !!highlightUserId && (
              (a._direction === 'given'    && a.to_user?.id   === highlightUserId) ||
              (a._direction === 'received' && a.from_user?.id === highlightUserId)
            );
            return (
              <AppreciationCard
                key={`${a._direction}-${a.id}`}
                a={a} direction={a._direction} highlighted={isHighlighted}
                appId={appId} me={me}
                onEdit={setEditingItem}
                onDeleted={id => setGiven(prev => prev.filter(x => x.id !== id))}
              />
            );
          })
      }
      {visibleCount < filtered.length && (
        <div style={{ textAlign: 'center', margin: '16px 0' }}>
          <button onClick={() => setVisibleCount(c => c + 20)} className="feed-load-more-btn">
            Load more ({filtered.length - visibleCount} remaining)
          </button>
        </div>
      )}
      {editingItem && (
        <AppreciationModal
          appId={appId} me={me} members={members}
          editing={editingItem}
          onDone={() => { setEditingItem(null); reload(); }}
          onClose={() => setEditingItem(null)}
        />
      )}
    </div>
  );
}

// ─── Feedback Panel ───────────────────────────────────────────────────────────

function FeedbackPanel({ appId, me, members, cycles, reloadKey, searchQuery }) {
  const api = useApi();
  const [received,     setReceived]     = useState([]);
  const [given,        setGiven]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState('');
  const [localKey,     setLocalKey]     = useState(0);
  const [visibleCount, setVisibleCount] = useState(30);
  const [editingItem,  setEditingItem]  = useState(null);

  const reload = () => setLocalKey(k => k + 1);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.getReceivedFeedback(appId), api.getGivenFeedback(appId)])
      .then(([r, g]) => { setReceived(r.items || []); setGiven(g.items || []); })
      .catch(() => setErr('Failed to load'))
      .finally(() => setLoading(false));
  }, [appId, reloadKey, localKey]);

  if (loading) return <div className="loading">Loading…</div>;
  const q = (searchQuery || '').toLowerCase().trim();
  // Single combined feed, newest first — no Received/Given tabs to switch between.
  const allList = [
    ...received.map(fb => ({ ...fb, _direction: 'received' })),
    ...given.map(fb => ({ ...fb, _direction: 'given' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const filtered = q
    ? allList.filter(fb =>
        (!fb.is_anonymous && uname(fb.from_user).toLowerCase().includes(q)) ||
        uname(fb.to_user).toLowerCase().includes(q) ||
        (fb.feedback_text || '').toLowerCase().includes(q)
      )
    : allList;
  const list = filtered.slice(0, visibleCount);

  return (
    <div>
      {err && <div className="error-message">{err}</div>}
      {list.length === 0
        ? <div className="feed-empty feed-empty--sm">
            <div className="feed-empty-icon">💬</div>
            <div className="feed-empty-text">{q ? `No results for "${q}"` : 'No feedback yet.'}</div>
          </div>
        : list.map(fb => (
            <FeedbackCard
              key={`${fb._direction}-${fb.id}`}
              fb={fb} direction={fb._direction} appId={appId} me={me}
              onEdit={setEditingItem}
              onDeleted={id => setGiven(prev => prev.filter(f => f.id !== id))}
            />
          ))
      }
      {visibleCount < filtered.length && (
        <div style={{ textAlign: 'center', margin: '16px 0' }}>
          <button onClick={() => setVisibleCount(c => c + 20)} className="feed-load-more-btn">
            Load more ({filtered.length - visibleCount} remaining)
          </button>
        </div>
      )}
      {editingItem && (
        <FeedbackModal
          appId={appId} me={me} members={members} cycles={cycles}
          editing={editingItem}
          onDone={() => { setEditingItem(null); reload(); }}
          onClose={() => setEditingItem(null)}
        />
      )}
    </div>
  );
}

// ─── Donut chart ──────────────────────────────────────────────────────────────

const DONUT_COLORS = ['#6f7bff', '#f0a93b', '#4ad295', '#c084fc', '#06b6d4'];

function DonutChart({ total, segments }) {
  const r = 32, cx = 40, cy = 40;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const totalCount = segments.reduce((s, [, c]) => s + c, 0) || 1;
  const arcs = segments.map(([tag, count], i) => {
    const dash = (count / totalCount) * circ;
    const arc = (
      <circle key={tag} r={r} cx={cx} cy={cy} fill="none"
        stroke={DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth={10}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={-offset}
        style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }}
      />
    );
    offset += dash;
    return arc;
  });
  return (
    <div className="feed-donut-wrap">
      <svg width={80} height={80}>
        <circle r={r} cx={cx} cy={cy} fill="none" stroke="var(--bg-muted)" strokeWidth={10} />
        {arcs}
      </svg>
      <div className="feed-donut-center">
        <div className="feed-donut-label">Total</div>
        <div className="feed-donut-total">{total}</div>
      </div>
    </div>
  );
}

// ─── Right panel ──────────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#6f7bff', '#f0a93b', '#4ad295', '#c084fc', '#06b6d4', '#ff6b6b', '#4ad295'];
const RANK_COLORS   = ['#f0a93b', '#4ad295', '#06b6d4'];

function FeedRightPanel({ appId, reloadKey, me, onNavigate, onTagClick, activeTag }) {
  const api = useApi();
  const [highlights, setHighlights] = useState({ new_posts: 0, appreciations: 0, comments: 0, mentions: 0 });
  const [topAppreciations, setTopAppreciations] = useState([]);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [showAllTrending, setShowAllTrending] = useState(false);

  useEffect(() => {
    api.getFeed(appId)
      .then(d => {
        if (d.highlights) setHighlights(d.highlights);
        // These are computed server-side over the requester's FULL visible post
        // set (not just the paged slice), so admins/managers — who see a much
        // larger post pool than a regular member — get accurate counts instead
        // of older appreciation/hashtag posts being crowded out by pagination.
        setTopAppreciations(d.top_appreciations || []);
        setTrendingTopics((d.trending_topics || []).map(t => [t.tag, t.count]));
      })
      .catch(() => {});
  }, [api, appId, reloadKey]);

  const statItems = [
    { label: 'New Posts',     count: highlights.new_posts,     color: '#4f8ef7' },
    { label: 'Appreciations', count: highlights.appreciations, color: '#f0a93b' },
    { label: 'Comments',      count: highlights.comments,      color: '#4ad295' },
    { label: 'Mentions',      count: highlights.mentions,      color: '#ff6b6b' },
  ];

  const topUsers = topAppreciations.filter(t => t.user);

  const trendingAll = trendingTopics;
  const trending = showAllTrending ? trendingAll : trendingAll.slice(0, 5);
  const trendTotal = trendingAll.reduce((s, [, c]) => s + c, 0);

  const monthLabel = new Date().toLocaleString('default', { month: 'short' }).toUpperCase();

  return (
    <div>
      {/* Today's Highlights */}
      <div className="feed-panel-card">
        <div className="feed-panel-header">
          <h3 className="feed-panel-title">Today's Highlights</h3>
          <button className="feed-panel-viewall" onClick={() => onNavigate?.('Feed')}>View All</button>
        </div>
        <div className="feed-highlights-list">
          {statItems.map(s => (
            <div key={s.label} className="feed-highlight-row">
              <div className="feed-highlight-icon" style={{ background: s.color + '22' }}>
                <div className="feed-highlight-dot" style={{ background: s.color }} />
              </div>
              <span className="feed-highlight-label">{s.label}</span>
              <span className="feed-highlight-num" style={{ color: s.color }}>{s.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Appreciations */}
      <div className="feed-panel-card">
        <div className="feed-panel-header">
          <h3 className="feed-panel-title">Top Appreciations · {monthLabel}</h3>
          <button className="feed-panel-viewall" onClick={() => onNavigate?.('Feed', { postFilter: 'appreciation', month: 'current' })}>View All</button>
        </div>
        {topUsers.length === 0
          ? <p className="feed-panel-empty">No appreciations yet</p>
          : topUsers.map((entry, i) => (
              <div
                key={i}
                className="feed-rank-item feed-rank-item--clickable"
                onClick={() => onNavigate?.('Appreciations', { highlightUserId: entry.user.id })}
              >
                <span className="feed-rank-num-text" style={{ color: RANK_COLORS[i] }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="feed-rank-avatar" style={{ background: entry.user?.photo_url ? 'transparent' : AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                  {entry.user?.photo_url
                    ? <img src={entry.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : uname(entry.user).charAt(0).toUpperCase()}
                </div>
                <span className="feed-rank-name">{uname(entry.user)}</span>
                <span className="feed-rank-count">{entry.count}</span>
              </div>
            ))
        }
      </div>

      {/* Trending Topics */}
      <div className="feed-panel-card">
        <div className="feed-panel-header">
          <h3 className="feed-panel-title">Trending Topics</h3>
        </div>
        {trendingAll.length === 0
          ? <p className="feed-panel-empty">No trending topics yet</p>
          : (
            <>
              <div className="feed-trend-pills">
                {trending.map(([tag, count]) => (
                  <span
                    key={tag}
                    className={`feed-trend-pill feed-trend-pill--clickable${activeTag && activeTag.has(tag) ? ' feed-trend-pill--active' : ''}`}
                    onClick={() => onTagClick?.(tag)}
                    title={`Filter feed by ${tag}`}
                  >
                    <span className="feed-trend-pill-tag">#{tag.replace('#', '')}</span>
                    <span className="feed-trend-pill-count">{count}</span>
                  </span>
                ))}
              </div>
              {trendingAll.length > 5 && (
                <button
                  className="feed-panel-viewall"
                  style={{ marginTop: 8, display: 'block' }}
                  onClick={() => setShowAllTrending(v => !v)}
                >
                  {showAllTrending ? 'View Less' : 'View More'}
                </button>
              )}
            </>
          )
        }
      </div>
    </div>
  );
}

// ─── Root page ────────────────────────────────────────────────────────────────

const VALID_TABS = ['Feed', 'Announcements', 'Appreciations', 'Feedback'];

export default function Feed() {
  const { appId }                                   = useParams();
  const api                                         = useApi();
  const { me: currentUser, myRole, searchQuery }    = useWorkspace();
  const [searchParams]                              = useSearchParams();

  const me      = currentUser?.id;
  const isAdmin = ['super_admin', 'admin'].includes(myRole);

  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Feed';
  const [activePanel,    setActivePanel]    = useState(initialTab);
  const [members,        setMembers]        = useState([]);
  const [departments,    setDepartments]    = useState([]);
  const [roles,          setRoles]          = useState([]);
  const [cycles,         setCycles]         = useState([]);
  const [tasks,          setTasks]          = useState([]);
  const [showAppreciate,  setShowAppreciate]  = useState(false);
  const [showFeedback,    setShowFeedback]    = useState(false);
  const [showPost,        setShowPost]        = useState(false);
  const [reloadKey,       setReloadKey]       = useState(0);
  const [highlightUserId, setHighlightUserId] = useState(null);
  const [hashFilter,      setHashFilter]      = useState(new Set());
  const [feedTypeFilter,  setFeedTypeFilter]  = useState(null);
  const [feedMonthFilter, setFeedMonthFilter] = useState(null);

  // Sync active tab when ?tab= param changes (e.g. from notification click while Feed is already mounted)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && VALID_TABS.includes(tab)) setActivePanel(tab);
  }, [searchParams]);

  useEffect(() => {
    if (!appId) return;
    Promise.all([
      api.listAppMembers(appId),
      api.departments.list(appId),
      api.roles.list(appId),
      api.listReviewCycles(appId),
      api.listTasks(appId),
    ]).then(([md, dd, rd, cd, td]) => {
      const raw = md.items || md.members || [];
      setMembers(raw.map(m => ({ id: m.user_id, ...m })).filter(m => m.id));
      setDepartments(dd.items || []);
      setRoles(rd.items || []);
      setCycles(cd.items || []);
      setTasks((td.items || []).map(t => ({ id: t.id, title: t.title })));
    }).catch(() => {/* non-critical */});
  }, [appId, me]);

  const reload = () => setReloadKey(k => k + 1);
  const handleDone = () => { setShowAppreciate(false); setShowFeedback(false); setShowPost(false); reload(); };

  return (
    <>
      <div className="feed-page">
        <div className="feed-main">
          <div className="feed-page-header">
            <div>
              <h1 className="feed-page-title">Feed</h1>
              <p className="feed-page-subtitle">Posts, appreciations and feedback — what your team is saying, recognising and shipping.</p>
            </div>

          </div>

          {activePanel === 'Feed' && (
            <FeedPanel
              appId={appId} me={me} isAdmin={isAdmin}
              members={members} departments={departments} roles={roles} cycles={cycles}
              onShowAppreciate={() => setShowAppreciate(true)}
              onShowFeedback={() => setShowFeedback(true)}
              onShowPost={() => setShowPost('post')}
              onShowPoll={() => setShowPost('poll')}
              reloadKey={reloadKey}
              searchQuery={searchQuery}
              preFilter={feedTypeFilter}
              monthFilter={feedMonthFilter}
              hashFilter={hashFilter}
              onClearHash={() => setHashFilter(new Set())}
              onRemoveHash={t => setHashFilter(prev => { const n = new Set(prev); n.delete(t); return n; })}
            />
          )}
          {activePanel === 'Announcements' && (
            <FeedPanel
              appId={appId} me={me} isAdmin={isAdmin}
              members={members} departments={departments} roles={roles} cycles={cycles}
              onShowAppreciate={() => setShowAppreciate(true)}
              onShowFeedback={() => setShowFeedback(true)}
              onShowPost={() => setShowPost('post')}
              onShowPoll={() => setShowPost('poll')}
              reloadKey={reloadKey}
              searchQuery={searchQuery}
              preFilter="announcement"
              hashFilter={hashFilter}
              onClearHash={() => setHashFilter(new Set())}
              onRemoveHash={t => setHashFilter(prev => { const n = new Set(prev); n.delete(t); return n; })}
            />
          )}
          {activePanel === 'Appreciations' && (
            <AppreciationsPanel
              appId={appId} me={me} members={members}
              onShowAppreciate={() => setShowAppreciate(true)}
              reloadKey={reloadKey}
              searchQuery={searchQuery}
              highlightUserId={highlightUserId}
            />
          )}
          {activePanel === 'Feedback' && (
            <FeedbackPanel
              appId={appId}
              me={me} members={members} cycles={cycles}
              onShowFeedback={() => setShowFeedback(true)}
              reloadKey={reloadKey}
              searchQuery={searchQuery}
            />
          )}
        </div>

        <div className="feed-right">
          <FeedRightPanel
            appId={appId}
            reloadKey={reloadKey}
            me={me}
            onNavigate={(panel, opts) => {
              setActivePanel(panel);
              setHighlightUserId(opts?.highlightUserId ?? null);
              setFeedTypeFilter(opts?.postFilter ?? null);
              setFeedMonthFilter(opts?.month ?? null);
            }}
            activeTag={hashFilter}
            onTagClick={(tag) => {
              setHashFilter(prev => {
                const next = new Set(prev);
                next.has(tag) ? next.delete(tag) : next.add(tag);
                return next;
              });
              setActivePanel('Feed');
            }}
          />
        </div>
      </div>

      {showAppreciate && (
        <AppreciationModal appId={appId} me={me} members={members} onDone={handleDone} onClose={() => setShowAppreciate(false)} />
      )}
      {showFeedback && (
        <FeedbackModal appId={appId} me={me} members={members} cycles={cycles} onDone={handleDone} onClose={() => setShowFeedback(false)} />
      )}
      {showPost && (
        <PostModal
          appId={appId} isAdmin={isAdmin} me={me}
          members={members} departments={departments} roles={roles}
          tasks={tasks}
          initialType={showPost === 'poll' ? 'poll' : 'post'}
          onDone={handleDone} onClose={() => setShowPost(false)}
        />
      )}
    </>
  );
}
